/* Progressive enhancement of Shopify's paginated collection section. */
if (!customElements.get('lyma-collection-scroll')) {
  class LymaCollectionScroll extends HTMLElement {
    static memory = new Map();

    connectedCallback() {
      const reconnect = this.initialized && this.grid === this.querySelector('#product-grid');
      this.grid = this.querySelector('#product-grid');
      this.pagination = this.querySelector('[data-collection-pagination]');
      this.status = this.querySelector('[data-collection-status]');
      this.moreButton = this.querySelector('[data-collection-more]');
      if (!this.grid || !this.status || !this.pagination || !this.moreButton || typeof window.IntersectionObserver !== 'function') return;
      this.lifecycle = new AbortController();
      if (!reconnect) {
        this.page = Number(this.dataset.page);
        this.nextURL = this.dataset.nextUrl || '';
        this.initialPage = this.page;
        this.initialIDs = [...this.grid.children].map((card) => card.dataset.productId).join(',');
        this.ids = new Set(this.initialIDs.split(',').filter(Boolean));
        this.loadedURLs = new Set();
        this.appendedHTML = '';
        this.context = this.contextFor(window.location.href, true);
      }
      this.suspended = false;
      this.loading = false;
      this.grid.setAttribute('aria-busy', 'false');
      this.removeAttribute('data-loading');
      this.tail = [];
      this.entryKey = (reconnect && this.entryKey) || history.state?.lymaCollectionEntry || `${Date.now()}-${Math.random().toString(36).slice(2)}`;
      history.replaceState({ ...history.state, searchParams: window.location.search.slice(1), lymaCollectionEntry: this.entryKey }, '');
      this.observer = new IntersectionObserver((entries) => {
        if (entries.some((entry) => entry.isIntersecting)) this.loadNext();
      }, { rootMargin: '0px 0px 600px 0px' });
      const options = { signal: this.lifecycle.signal };
      this.moreButton.addEventListener('click', () => this.loadNext(true), options);
      document.addEventListener('lyma:collection:before-render', () => {
        this.save();
        this.suspend();
      }, options);
      window.addEventListener('pagehide', () => { this.save(); this.suspend(); }, options);
      window.addEventListener('pageshow', (event) => {
        if (event.persisted) {
          this.suspended = false;
          this.loading = false;
          this.removeAttribute('data-loading');
          this.grid.setAttribute('aria-busy', 'false');
          this.observe();
        }
      }, options);
      this.setAttribute('data-enhanced', '');
      this.toggleAttribute('data-has-pages', Boolean(this.nextURL) || this.page > 1);
      this.update();
      this.initialized = true;
      (reconnect ? Promise.resolve() : this.restore()).finally(() => {
        if (this.isConnected && !this.suspended) this.observe();
      });
    }

    disconnectedCallback() {
      this.suspend();
      this.lifecycle?.abort();
      this.revealTail();
      cancelAnimationFrame(this.restoreFrame);
    }

    contextFor(value, includePage = false) {
      const url = new URL(value, window.location.href);
      for (const key of [...url.searchParams.keys()]) {
        if (key === 'section_id' || key === 'sections' || key.startsWith('preview_') || ['_fd', '_sc', '_ab'].includes(key) || (!includePage && key === 'page')) url.searchParams.delete(key);
      }
      url.searchParams.sort();
      return `${url.origin}${url.pathname}?${url.searchParams}`;
    }

    suspend() {
      this.suspended = true;
      this.observer?.disconnect();
      this.request?.abort();
    }

    observe() {
      this.observer.disconnect();
      if (this.nextURL && !this.suspended && !this.restoring && !this.hasAttribute('data-error')) this.observer.observe(this.status);
    }

    update() {
      this.toggleAttribute('data-pending', Boolean(this.nextURL));
      this.moreButton.hidden = !this.nextURL;
      if (this.nextURL) {
        const section = this.closest('.shopify-section');
        const nodes = [];
        for (let node = section?.nextElementSibling; node; node = node.nextElementSibling) nodes.push(node);
        nodes.push(...document.querySelectorAll('.shopify-section-group-footer-group'));
        nodes.forEach((node) => {
          if (this.tail.some((item) => item.node === node)) return;
          this.tail.push({ node, hidden: node.hidden });
          node.hidden = true;
          node.setAttribute('data-lyma-collection-deferred', '');
        });
      } else {
        this.revealTail();
        this.observer.disconnect();
      }
    }

    revealTail() {
      this.tail?.forEach(({ node, hidden }) => {
        node.hidden = hidden;
        node.removeAttribute('data-lyma-collection-deferred');
      });
      this.tail = [];
    }

    append(cards, restoring = false) {
      const fragment = document.createDocumentFragment();
      let added = 0;
      cards.forEach((card) => {
        const id = card.dataset.productId;
        if (!id || this.ids.has(id)) return;
        this.ids.add(id);
        card.querySelectorAll('img').forEach((image) => {
          image.loading = 'lazy';
          image.setAttribute('loading', 'lazy');
          image.removeAttribute('fetchpriority');
        });
        // Existing cards and their listeners are never replaced on append.
        card.classList.add('scroll-trigger--cancel');
        this.appendedHTML += card.outerHTML;
        if (!restoring) card.classList.add('lyma-collection-new-card');
        fragment.appendChild(card);
        added++;
      });
      this.grid.appendChild(fragment);
      return added;
    }

    async loadNext(focusNew = false) {
      if (this.loading || this.suspended || !this.nextURL || !this.isConnected) return false;
      this.loading = true;
      this.observer.disconnect();
      this.removeAttribute('data-error');
      this.setAttribute('data-loading', '');
      this.moreButton.disabled = true;
      this.grid.setAttribute('aria-busy', 'true');
      this.querySelector('[data-collection-message]').textContent = 'Producten laden…';
      const request = this.request = new AbortController();
      try {
        const url = new URL(this.nextURL, window.location.href);
        const page = Number(url.searchParams.get('page'));
        if (url.origin !== window.location.origin || this.contextFor(url) !== this.contextFor(window.location.href) || page !== this.page + 1 || this.loadedURLs.has(url.href)) throw new Error('Invalid collection page');
        const pageURL = url.href;
        url.searchParams.set('section_id', this.dataset.sectionId);
        const response = await fetch(url.href, { signal: request.signal, credentials: 'same-origin' });
        if (!response.ok) throw new Error('Collection request failed');
        const html = new DOMParser().parseFromString(await response.text(), 'text/html');
        if (request.signal.aborted || !this.isConnected || this.suspended) return false;
        const incoming = html.querySelector('lyma-collection-scroll');
        const grid = incoming?.querySelector('#product-grid');
        if (!grid || incoming.dataset.sectionId !== this.dataset.sectionId || Number(incoming.dataset.page) !== page) throw new Error('Invalid collection section');
        const cards = [...grid.children];
        if (!cards.length || cards.some((card) => !card.dataset.productId)) throw new Error('Missing collection cards');
        const firstNewCard = cards.find((card) => !this.ids.has(card.dataset.productId));
        const added = this.append(cards, this.restoring);
        this.loadedURLs.add(pageURL);
        this.page = page;
        this.nextURL = incoming.dataset.nextUrl || '';
        this.pagination.innerHTML = incoming.querySelector('[data-collection-pagination]')?.innerHTML || '';
        this.querySelector('[data-collection-message]').textContent = `${added} producten toegevoegd. ${this.ids.size} producten geladen.`;
        this.update();
        if (focusNew && added) firstNewCard.querySelector('a[href], button')?.focus();
        return true;
      } catch (error) {
        if (request.signal.aborted || !this.isConnected || this.suspended) return false;
        this.setAttribute('data-error', '');
        this.querySelector('[data-collection-message]').textContent = 'Laden is niet gelukt. Probeer opnieuw of gebruik de paginalinks.';
        this.moreButton.textContent = 'Opnieuw proberen';
        return false;
      } finally {
        if (this.request === request) {
          this.loading = false;
          this.removeAttribute('data-loading');
          this.grid.setAttribute('aria-busy', 'false');
          this.moreButton.disabled = false;
          if (!this.hasAttribute('data-error')) this.moreButton.textContent = 'Meer producten laden';
          if (this.isConnected) this.observe();
        }
      }
    }

    save() {
      if (!this.grid || this.restoring) return;
      const anchor = [...this.grid.children].find((card) => card.getBoundingClientRect().bottom > 0);
      const snapshot = {
        anchor: anchor ? { id: anchor.dataset.productId, top: anchor.getBoundingClientRect().top } : null,
        context: this.context, section: this.dataset.sectionId, initialIDs: this.initialIDs,
        page: this.page, nextURL: this.nextURL, y: window.scrollY, time: Date.now(),
        // Large histories retain their position/page count and replay batches on back.
        html: this.appendedHTML.length < 1500000 ? this.appendedHTML : null,
        pagination: this.pagination.innerHTML,
      };
      LymaCollectionScroll.memory.set(this.entryKey, snapshot);
      while (LymaCollectionScroll.memory.size > 3) LymaCollectionScroll.memory.delete(LymaCollectionScroll.memory.keys().next().value);
      try {
        const key = `lyma-collection:${this.entryKey}`;
        const keys = JSON.parse(sessionStorage.getItem('lyma-collection:keys') || '[]').filter((item) => item !== key);
        keys.push(key);
        while (keys.length > 3) sessionStorage.removeItem(keys.shift());
        sessionStorage.setItem('lyma-collection:keys', JSON.stringify(keys));
        try { sessionStorage.setItem(key, JSON.stringify(snapshot)); }
        catch { sessionStorage.setItem(key, JSON.stringify({ ...snapshot, html: null })); }
      } catch { /* Storage is optional; native bfcache remains available. */ }
    }

    async restore() {
      let snapshot = LymaCollectionScroll.memory.get(this.entryKey);
      try { snapshot ||= JSON.parse(sessionStorage.getItem(`lyma-collection:${this.entryKey}`) || 'null'); } catch { /* Private browsing/quota. */ }
      if (!snapshot || snapshot.context !== this.context || snapshot.section !== this.dataset.sectionId || snapshot.initialIDs !== this.initialIDs || Date.now() - snapshot.time > 30 * 60 * 1000) return;
      this.restoring = true;
      try {
        if (typeof snapshot.html === 'string') {
          const html = new DOMParser().parseFromString(`<ul>${snapshot.html}</ul>`, 'text/html');
          this.append([...html.querySelector('ul').children], true);
          this.page = snapshot.page;
          this.nextURL = snapshot.nextURL;
          this.pagination.innerHTML = snapshot.pagination;
          this.update();
        } else {
          while (this.page < snapshot.page && this.nextURL && !this.suspended) {
            if (!await this.loadNext()) break;
          }
        }
        if (this.isConnected && !this.suspended) {
          this.restoreFrame = requestAnimationFrame(() => {
            if (!this.isConnected || this.suspended) return;
            const anchor = snapshot.anchor && [...this.grid.children].find((card) => card.dataset.productId === snapshot.anchor.id);
            const top = anchor ? window.scrollY + anchor.getBoundingClientRect().top - snapshot.anchor.top : snapshot.y;
            window.scrollTo({ top, behavior: 'instant' });
          });
        }
      } finally { this.restoring = false; }
    }
  }
  customElements.define('lyma-collection-scroll', LymaCollectionScroll);
}
